import { useUserByUid } from '../../api/users.js';
import type { Membership, Tenant } from '../../api/types.js';
import { AccessPills } from './AccessPills.js';

const ALTYOR_DOMAIN = /@altyor\.com$/i;

export interface AggregatedMember {
  uid: string;
  memberships: Array<{ membership: Membership; tenant: Tenant }>;
}

interface Props {
  user: AggregatedMember;
  onEditRole: (entry: { membership: Membership; tenant: Tenant }) => void;
  onRevoke: (entry: { membership: Membership; tenant: Tenant }) => void;
}

function isExternalEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return !ALTYOR_DOMAIN.test(email);
}

function initials(name: string | null | undefined, email: string | null | undefined): string {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s.@-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

// REQ-FE-029h — One row per user (not per membership). Aggregates the
// caller's access across tenants into a multi-pill display. External
// users (non-`@altyor.com`) get a salmon left border + `EXT` badge per
// mockup `09-admin-members.png`.
export function MemberRow({ user, onEditRole, onRevoke }: Props) {
  const lookup = useUserByUid(user.uid);
  const u = lookup.data ?? null;
  const displayName = u?.displayName ?? u?.email ?? user.uid;
  const email = u?.email ?? null;
  const external = isExternalEmail(email);
  return (
    <li
      className={`admin-member-row${external ? ' admin-member-row--external' : ''}`}
      data-testid={`admin-user-row-${user.uid}`}
      data-external={external ? 'true' : 'false'}
    >
      <div className="admin-member-row__avatar" aria-hidden="true">
        {initials(u?.displayName ?? null, email)}
      </div>
      <div className="admin-member-row__id">
        <div className="admin-member-row__name">{displayName}</div>
        <div className="muted admin-member-row__email">{email ?? user.uid}</div>
      </div>
      <div className="admin-member-row__access">
        <AccessPills entries={user.memberships} onEditRole={onEditRole} onRevoke={onRevoke} />
      </div>
      {external ? (
        <span
          className="pill pill--warning admin-member-row__ext-badge"
          data-testid={`admin-user-row-${user.uid}-ext`}
        >
          Externe
        </span>
      ) : null}
    </li>
  );
}
